import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Cloud,
  House,
  Kanban,
  CheckCircle2,
  FileText,
  Settings,
  Search,
  Bell,
  LogOut,
} from "lucide-react";
import { useAuth } from "../../app/providers/auth-provider";

const NAV_ITEMS = [
  { id: "files",    icon: Cloud,         title: "Drive",    path: "/files" },
  { id: "kanban",   icon: Kanban,        title: "Projects", path: "/kanban" },
  { id: "todos",    icon: CheckCircle2,  title: "Tasks",    path: "/todos" },
  { id: "notes",    icon: FileText,      title: "Notes",    path: "/notes" },
] as const;

const MOBILE_NAV_ITEMS = [
  { id: "home", icon: House, title: "Home", path: "/" },
  ...NAV_ITEMS,
] as const;

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const activeItem = NAV_ITEMS.find((item) => item.path === location.pathname);
  const isSettings = location.pathname === "/settings";
  const viewTitle = isSettings
    ? "Settings"
    : activeItem
      ? activeItem.title
      : null;

  return (
    <div className="bg-surface text-on-surface min-h-screen flex font-sans">
      {/* Sidebar */}
      <nav className="fixed left-0 top-0 bottom-0 z-50 hidden w-[64px] h-screen flex-col items-center py-6 bg-surface-container-low border-r border-outline-variant md:flex">
        <div className="mb-8 flex items-center justify-center">
          <div
            className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-on-primary font-h3 font-bold text-sm cursor-pointer select-none"
            onClick={() => navigate("/")}
            title="Sandok"
          >
            S
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-1 w-full px-2 items-center">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                title={item.title}
                className={`p-2 rounded-lg flex items-center justify-center w-10 h-10 transition-colors scale-95 duration-100 transition-transform ${
                  active
                    ? "bg-secondary-container text-primary"
                    : "text-secondary hover:bg-surface-container-high"
                }`}
              >
                <Icon size={20} className={active ? "fill-primary/20" : ""} />
              </button>
            );
          })}
        </div>

        <div className="mt-auto w-full px-2 flex flex-col gap-2 items-center">
          <button
            className={`p-2 transition-colors rounded-lg w-10 h-10 flex items-center justify-center ${
              isSettings
                ? "bg-secondary-container text-primary"
                : "text-secondary hover:bg-surface-container-high"
            }`}
            onClick={() => navigate("/settings")}
            title="Settings"
          >
            <Settings size={20} className={isSettings ? "fill-primary/20" : ""} />
          </button>
          <button
            className="p-2 transition-colors rounded-lg w-10 h-10 flex items-center justify-center text-secondary hover:bg-surface-container-high"
            onClick={handleSignOut}
            title="Sign out"
          >
            <LogOut size={20} />
          </button>
        </div>
      </nav>

      {/* Top bar */}
      <header className="fixed top-0 right-0 left-0 md:left-[64px] z-40 px-4 sm:px-6 bg-surface-container-lowest h-16 flex items-center justify-between border-b border-outline-variant transition-colors">
        <div className="flex items-center gap-4">
          <h1 className="text-h3 font-black text-primary tracking-tight cursor-default">
            Workspace
          </h1>
          {viewTitle && (
            <div className="hidden md:flex items-center select-none">
              <span className="text-primary font-label-sm border-b-2 border-primary pb-0.5">
                {viewTitle}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="relative hidden sm:block w-64 group">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary transition-colors"
            />
            <input
              type="text"
              placeholder="Search Workspace..."
              className="w-full bg-surface-container-lowest border border-outline-variant rounded pl-9 pr-3 py-1.5 font-body-md text-primary placeholder:text-outline focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            <button className="text-secondary hover:text-primary p-2 rounded hover:bg-surface-container-low transition-colors">
              <Bell size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="mt-16 md:ml-[64px] flex-1 min-w-0 max-h-[calc(100vh-64px)] overflow-hidden flex flex-col bg-surface pb-20 md:pb-0">
        <Outlet />
      </main>

      <nav
        aria-label="Primary"
        className="fixed bottom-0 left-0 right-0 z-50 grid grid-cols-6 border-t border-outline-variant bg-surface-container-lowest px-1 pb-[env(safe-area-inset-bottom)] shadow-[0_-1px_6px_rgba(0,0,0,0.06)] md:hidden"
      >
        {MOBILE_NAV_ITEMS.map((item) => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.path)}
              aria-current={active ? "page" : undefined}
              className={`flex min-h-16 min-w-0 flex-col items-center justify-center gap-1 rounded px-1 py-2 font-label-sm transition-colors ${
                active
                  ? "text-primary"
                  : "text-secondary hover:bg-surface-container-low"
              }`}
            >
              <Icon size={20} className={active ? "fill-primary/20" : ""} />
              <span className="max-w-full truncate">{item.title}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => navigate("/settings")}
          aria-current={isSettings ? "page" : undefined}
          className={`flex min-h-16 min-w-0 flex-col items-center justify-center gap-1 rounded px-1 py-2 font-label-sm transition-colors ${
            isSettings
              ? "text-primary"
              : "text-secondary hover:bg-surface-container-low"
          }`}
        >
          <Settings size={20} className={isSettings ? "fill-primary/20" : ""} />
          <span className="max-w-full truncate">Settings</span>
        </button>
      </nav>
    </div>
  );
}
